/* eslint-disable react-hooks/exhaustive-deps */
import * as api from "../../../../shared/lib/api";
import JsonCodeEditor from "../../../../shared/components/json-code-editor";
import { LooseObject } from "../../../../shared/types/core";
import { useEffect, useState } from "react";
import {
	ResponseQueryProps,
	RetrieveCallResponseProps,
} from "../../types/query";
import useWindowSize from "../../../../shared/hooks/use-window-size";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
	Accordion,
	AccordionDetails,
	AccordionSummary,
	Alert,
	Box,
	Button,
	CircularProgress,
	Grid,
	Modal,
	Typography,
} from "@mui/material";
import { Allotment } from "allotment";
import "allotment/dist/style.css";

export type ResolvedResponseItem = {
	id: string;
	name: string;
	// null quando a busca do retorno dessa resposta falhou ou não retornou dado
	data: LooseObject | null;
	integrationResponse: LooseObject[] | null;
};

interface ResponseModalProps {
	open: boolean;
	onClose: () => void;
	getResponses: () => Promise<Pick<ResponseQueryProps, "id" | "name">[]>;
}

export default function ResponseModal({
	open,
	onClose,
	getResponses,
}: ResponseModalProps) {
	const { width } = useWindowSize();
	const isNarrow = !!width && width < 768;
	const [loading, setLoading] = useState(false);
	const [resolvedResponses, setResolvedResponses] = useState<
		ResolvedResponseItem[]
	>([]);

	useEffect(() => {
		if (!open) return;

		let cancelled = false;
		setLoading(true);
		setResolvedResponses([]);

		(async () => {
			let results: ResolvedResponseItem[] = [];

			try {
				const responses = await getResponses();

				results = await Promise.all(
					responses.map(async (response): Promise<ResolvedResponseItem> => {
						try {
							const detail = (await api.get(
								`call/retrieve/response/${response.id}/`,
							)) as RetrieveCallResponseProps;

							return {
								id: response.id,
								name: response.name,
								data: detail.content.data,
								integrationResponse:
									detail.content.integration_response ?? null,
							};
						} catch {
							return {
								id: response.id,
								name: response.name,
								data: null,
								integrationResponse: null,
							};
						}
					}),
				);
			} catch {
				results = [];
			}

			if (!cancelled) {
				setResolvedResponses(results);
				setLoading(false);
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [open]);

	return (
		<Modal
			open={open}
			onClose={onClose}
			aria-labelledby="response-modal-title"
			sx={{ "@media print": { display: "none" } }}
		>
			<Box
				sx={{
					position: "absolute",
					top: "50%",
					left: "50%",
					transform: "translate(-50%, -50%)",
					width: "90%",
					maxWidth: 1100,
					bgcolor: "background.paper",
					boxShadow: 24,
					p: 4,
				}}
			>
				<Typography
					id="response-modal-title"
					variant="h6"
					component="h2"
					textAlign="center"
					sx={{ mb: 2 }}
				>
					Retorno da consulta
				</Typography>

				<div style={{ overflow: "auto", maxHeight: "70vh" }}>
					{loading ? (
						<Box sx={{ display: "flex", justifyContent: "center", py: 4 }}>
							<CircularProgress size={32} />
						</Box>
					) : resolvedResponses.length === 0 ? (
						<Alert severity="info">
							Nenhum retorno disponível para esta consulta.
						</Alert>
					) : (
						resolvedResponses.map((response) => (
							<Accordion key={response.id} disableGutters sx={{ mb: 1 }}>
								<AccordionSummary expandIcon={<ExpandMoreIcon />}>
									<Typography fontWeight={600}>{response.name}</Typography>
								</AccordionSummary>
								<AccordionDetails>
									<Box sx={{ height: 340 }}>
										<Allotment vertical={isNarrow} minSize={160}>
											<Allotment.Pane>
												<ResponseColumn
													label="Fornecedor"
													value={response.data}
												/>
											</Allotment.Pane>
											<Allotment.Pane>
												<ResponseColumn
													label="CheckAuto"
													value={response.integrationResponse}
												/>
											</Allotment.Pane>
										</Allotment>
									</Box>
								</AccordionDetails>
							</Accordion>
						))
					)}
				</div>

				<Grid
					gap={3}
					container
					marginTop={3}
					alignItems="center"
					justifyContent="center"
				>
					<Button
						variant="outlined"
						size="large"
						color="info"
						onClick={onClose}
					>
						Fechar
					</Button>
				</Grid>
			</Box>
		</Modal>
	);
}

interface ResponseColumnProps {
	label: string;
	value: LooseObject | LooseObject[] | null;
}

function ResponseColumn({ label, value }: ResponseColumnProps) {
	return (
		<Box
			sx={{ height: "100%", display: "flex", flexDirection: "column", px: 1 }}
		>
			<Typography
				variant="caption"
				fontWeight={700}
				color="text.secondary"
				sx={{
					display: "block",
					flexShrink: 0,
					py: 0.5,
					bgcolor: "background.paper",
				}}
			>
				{label}
			</Typography>
			<Box sx={{ flex: 1, overflow: "auto" }}>
				{value ? (
					<JsonCodeEditor
						value={JSON.stringify(value, null, 2)}
						readOnly
						minHeight="200px"
					/>
				) : (
					<Alert severity="info">
						Retorno indisponível para esta resposta.
					</Alert>
				)}
			</Box>
		</Box>
	);
}
